/**
 * Font pairings. A design needs three voices (headings, body text, and a script for flourishes)
 * plus Arabic faces that sit well beside them, so people pick a pairing rather than a font.
 *
 * Every family here is embedded in the players (see scripts/generate-menu-fonts.mjs), so a
 * screen draws the same letters offline as the dashboard preview does online. Latin faces carry
 * no Arabic glyphs, so Arabic text falls through to the Arabic family next in each stack.
 */

export interface MenuFont {
  id: string;
  name: string;
  /** One line describing the feel, for the picker. */
  description: string;
  heading: string;
  /** Weight to ask the heading face for; single-weight display faces must not be faux-bolded. */
  headingWeight: number;
  body: string;
  script: string;
}

const SANS_AR = "'MB Cairo', 'Noto Sans Arabic', Tahoma, sans-serif";
const DISPLAY_AR = "'MB Lalezar', 'MB Cairo', Tahoma, sans-serif";
const SERIF_AR = "'MB Amiri', 'Noto Naskh Arabic', serif";
const SCRIPT_AR = "'MB Ruqaa', 'MB Amiri', serif";
const BODY = `'MB Montserrat', ${SANS_AR}`;

export const MENU_FONTS: MenuFont[] = [
  {
    id: 'modern',
    name: 'Modern',
    description: 'Clean and even',
    heading: `'MB Montserrat', ${SANS_AR}`,
    headingWeight: 800,
    body: BODY,
    script: `'MB Kaushan', ${SCRIPT_AR}`,
  },
  {
    id: 'cafe',
    name: 'Café',
    description: 'Tall capitals with a brush script',
    heading: `'MB Oswald', ${DISPLAY_AR}`,
    headingWeight: 700,
    body: BODY,
    script: `'MB Kaushan', ${SCRIPT_AR}`,
  },
  {
    id: 'street',
    name: 'Street food',
    description: 'Big condensed headlines',
    heading: `'MB Bebas Neue', ${DISPLAY_AR}`,
    headingWeight: 400,
    body: BODY,
    script: `'MB Pacifico', ${SCRIPT_AR}`,
  },
  {
    id: 'chalk',
    name: 'Chalk',
    description: 'Hand-lettered, like a chalkboard',
    heading: `'MB Marker', ${DISPLAY_AR}`,
    headingWeight: 400,
    body: BODY,
    script: `'MB Pacifico', ${SCRIPT_AR}`,
  },
  {
    id: 'bold',
    name: 'Bold',
    description: 'Heavy poster capitals',
    heading: `'MB Anton', ${DISPLAY_AR}`,
    headingWeight: 400,
    body: BODY,
    script: `'MB Kaushan', ${SCRIPT_AR}`,
  },
  {
    id: 'classic',
    name: 'Classic',
    description: 'Elegant serif',
    heading: `'MB Playfair', ${SERIF_AR}`,
    headingWeight: 700,
    body: BODY,
    script: `'MB Pacifico', ${SCRIPT_AR}`,
  },
];

export function menuFont(id: string): MenuFont {
  return MENU_FONTS.find((f) => f.id === id) ?? MENU_FONTS[0]!;
}
